import React, { FunctionComponent, useCallback, useRef } from 'react'
import { FormattedMessage } from 'react-intl'

import BottomSheetGorhom from '@gorhom/bottom-sheet'
import styled, { useTheme } from 'styled-components/native'

import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { ExternalLinkIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/ExternalLinkIcon'
import { XIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/XIcon'
import { ProviderCalendarPreview } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/illustrations/ProviderCalendarPreview'
import { PressableOpacity } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/pressableOpacity/PressableOpacity'
import { PrimaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/primaryButton/PrimaryButton'
import { TextButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/textButton/TextButton'
import { Title } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { Modal } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/modal/Modal'
import { TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

type CalendarTransitionModalProps = {
  onUpdatePressed: () => void
  onClosePressed: () => void
  onHowItWorksPressed: () => void
  isVisible?: boolean
}

const Container = styled.View({
  flexDirection: 'column',
  alignSelf: 'flex-start',
  alignItems: 'center',
})

const Header = styled.View(({ theme }) => ({
  flexDirection: 'column',
  alignItems: 'center',
  marginTop: theme.spacing['8px'],
}))

const Row = styled.View(({ theme }) => ({
  flexDirection: 'row',
  gap: theme.spacing['16px'],
  alignSelf: 'flex-end',
  alignItems: 'center',
  marginTop: theme.spacing['48px'],
}))

const LinkButton = styled(TextButton)({
  alignSelf: 'center',
})

const ModalBodyText = styled(BodyText)(({ theme }) => ({
  marginTop: theme.spacing['16px'],
}))

const HeaderText = styled(Title)(({ theme }) => ({
  marginTop: theme.spacing['24px'],
}))

const CloseButton = styled(PressableOpacity)({
  alignSelf: 'flex-end',
})

export const CalendarTransitionModal: FunctionComponent<CalendarTransitionModalProps> = ({
  onUpdatePressed,
  onClosePressed,
  onHowItWorksPressed,
  isVisible = false,
}) => {
  const theme = useTheme() as ThemeType
  const ref = useRef<BottomSheetGorhom>(null)
  const openBottomSheet = useCallback(() => {
    ref.current?.expand()
  }, [])
  return (
    <Modal
      visible={isVisible}
      modalCardBackgroundImage={`linear-gradient(to bottom, ${theme.colors.backgroundHeaderPeriwinkle} 221px, ${theme.colors.backgroundPrimary} 0%)`}
      bottomSheetRef={ref}
      snapPoints={[550]}
      width='544px'
      onLayout={openBottomSheet}
      modalContents={
        <Container testID={tID('CalendarTransitionModal')}>
          <CloseButton
            hitSlop={60}
            testID={tID('CalendarTransitionModal-close-button')}
            onPress={() => onClosePressed?.()}
          >
            <XIcon size={24} fillColor={theme.colors.iconDefault} />
          </CloseButton>
          <Header>
            <ProviderCalendarPreview />
            <HeaderText
              textAlign='center'
              size={TitleSize.MEDIUM}
              text={
                <FormattedMessage
                  defaultMessage='Update to the new Lyra calendar'
                  description='Title for provider calendar update modal'
                />
              }
            />
            <ModalBodyText
              textAlign='center'
              color={theme.colors.textSecondary}
              text={
                <FormattedMessage
                  defaultMessage='We’ve introduced a new calendar view to make managing your schedule and availability easier. Simply confirm your synced calendars and set up your availability.'
                  description='Body text for provider calendar update modal'
                />
              }
            />
          </Header>
          <Row>
            <LinkButton
              testID={tID('CalendarTransitionModal-how-it-works-button')}
              rightIcon={<ExternalLinkIcon strokeColor={theme.colors.iconActive} />}
              text={
                <FormattedMessage
                  defaultMessage='How it works'
                  description='Button to learn more about the provider calendar update'
                />
              }
              onPress={onHowItWorksPressed}
            />
            <PrimaryButton
              testID={tID('CalendarTransitionModal-update-button')}
              text={
                <FormattedMessage
                  defaultMessage='Update now'
                  description='Button to begin update to provider calendar'
                />
              }
              onPress={onUpdatePressed}
            />
          </Row>
        </Container>
      }
      onRequestClose={() => onClosePressed?.()}
      onCloseEnd={() => onClosePressed?.()}
    />
  )
}
